import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { AssignableDashboardsDocument, DeleteDashboardDocument } from '../../graphql/__generated__/types';
import { catalogueRefusalOf } from './refusals';

interface Props {
    entry: { id: string; name: string };
    accountName: string;
    onClose: () => void;
}

// Deleting cascades and is irreversible: the entry and everyone's habilitation are gone for good.
// holderCount is only known after the gesture; no query returns it, so nothing else can say it later.
const DeleteDashboardDialog = ({ entry, accountName, onClose }: Props) => {
    const [acknowledged, setAcknowledged] = useState(false);

    const [remove, { loading, error, data }] = useMutation(DeleteDashboardDocument, {
        refetchQueries: [AssignableDashboardsDocument],
        awaitRefetchQueries: true,
        // Same eviction as the declaration: the onboarding journey reads this catalogue under its own variables.
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'assignableDashboards' });
            cache.gc();
        },
    });

    const done = data?.deleteDashboard;
    const holders = done?.holderCount ?? 0;

    const submit = () => {
        remove({ variables: { dashboardId: entry.id } }).catch(() => {});
    };

    return (
        <Modal open onClose={onClose} title={c('Dashboard catalogue').t`Delete this dashboard`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{entry.name}</b>
                        <span className="text-text-600"> · {accountName}</span>
                    </p>

                    {done ? (
                        <Alert type="success" title={c('Dashboard catalogue').t`The entry is deleted.`}>
                            <p className="text-sm">
                                {holders === 0
                                    ? c('Dashboard catalogue')
                                          .t`Nobody held it, so this unplugged no one. The same Metabase dashboard may be declared again on this account whenever needed.`
                                    : ngettext(
                                          msgid`${holders} person held it and has been unplugged: the entry and their habilitation are gone for good.`,
                                          `${holders} people held it and have been unplugged: the entry and their habilitations are gone for good.`,
                                          holders
                                      )}
                            </p>
                        </Alert>
                    ) : (
                        <>
                            <Alert type="warning">
                                {c('Dashboard catalogue')
                                    .t`Deleting destroys the entry and everyone's habilitation to it, for good: it cannot be undone. How many people that unplugs is shown once it is done; it cannot be counted beforehand. This gesture removes everything, so to only take the entry out of the catalogue while keeping who had it, close it another way.`}
                            </Alert>

                            <label className="flex items-start gap-2 text-sm text-text-900">
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={acknowledged}
                                    onChange={(event) => setAcknowledged(event.target.checked)}
                                />
                                <span>
                                    {c('Dashboard catalogue')
                                        .t`I understand this is permanent and unplugs everyone who holds it.`}
                                </span>
                            </label>

                            {error && <RefusalNotice refusal={catalogueRefusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button category="destructive" loading={loading} disabled={!acknowledged} onClick={submit}>
                            {c('Dashboard catalogue').t`Delete permanently`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default DeleteDashboardDialog;
